// Exchange rates for a past date: reading the date a user typed, asking the
// backend, and wording the answer the same way every time.
import { apiRequest } from './apiClient';

// Currencies offered in the lookup (all published by the European Central Bank).
export const HISTORY_CURRENCIES = [
  { code: 'USD', label: 'USD (US Dollar)' },
  { code: 'IDR', label: 'IDR (Indonesian Rupiah)' },
  { code: 'EUR', label: 'EUR (Euro)' },
  { code: 'JPY', label: 'JPY (Japanese Yen)' },
  { code: 'SGD', label: 'SGD (Singapore Dollar)' },
  { code: 'GBP', label: 'GBP (British Pound)' },
  { code: 'AUD', label: 'AUD (Australian Dollar)' },
  { code: 'CNY', label: 'CNY (Chinese Yuan)' },
  { code: 'MYR', label: 'MYR (Malaysian Ringgit)' },
  { code: 'INR', label: 'INR (Indian Rupee)' },
  { code: 'KRW', label: 'KRW (South Korean Won)' },
  { code: 'THB', label: 'THB (Thai Baht)' }
];

// The first day the European Central Bank published reference rates
export const EARLIEST_RATE_DATE = '1999-01-04';

export const DATE_EXAMPLES = '"yesterday", "last Monday", "17 Sep 2025" or "2025-09-17"';

const MONTHS = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3, may: 4,
  jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7, sep: 8, sept: 8, september: 8,
  oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11
};

const WEEKDAYS = {
  sun: 0, sunday: 0, mon: 1, monday: 1, tue: 2, tues: 2, tuesday: 2, wed: 3, wednesday: 3,
  thu: 4, thur: 4, thurs: 4, thursday: 4, fri: 5, friday: 5, sat: 6, saturday: 6
};

const COUNT_WORDS = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10
};

const pad = (value) => String(value).padStart(2, '0');

// Calendar date in the user's own time zone, as YYYY-MM-DD.
export const toIsoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const fromIsoDate = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const makeDate = (year, monthIndex, day) => {
  const date = new Date(year, monthIndex, day);
  // Dates such as 30 February roll over into the next month
  const isReal = date.getFullYear() === year && date.getMonth() === monthIndex && date.getDate() === day;
  return isReal ? date : null;
};

const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

// Same day of the month, or its last day when the month is shorter.
const addMonths = (date, months) => {
  const first = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return new Date(first.getFullYear(), first.getMonth(), Math.min(date.getDate(), lastDay));
};

const shift = (date, count, unit) => {
  if (unit === 'day') return addDays(date, -count);
  if (unit === 'week') return addDays(date, -7 * count);
  if (unit === 'month') return addMonths(date, -count);
  return addMonths(date, -12 * count);
};

// Reads a date written as text. Returns { date: 'YYYY-MM-DD' } or { error }.
// Accepts ISO dates, "today"/"yesterday", "3 days ago", "last week",
// "last Monday" and dates with the month as a word ("17 Sept 2025").
export const parseDateInput = (input, now = new Date()) => {
  const text = String(input || '')
    .toLowerCase()
    .replace(/[,.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^(on|at|for|from) /, '')
    .replace(/^the /, '');
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const found = (date) => ({ date: toIsoDate(date) });
  const notReal = { error: 'That date does not exist. Check the day and month.' };

  if (!text) return { error: `Enter a date, for example ${DATE_EXAMPLES}.` };

  let match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) {
    const date = makeDate(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return date ? found(date) : notReal;
  }

  if (text === 'today' || text === 'now') return found(today);
  if (text === 'yesterday') return found(addDays(today, -1));
  if (text === 'tomorrow') return found(addDays(today, 1));
  if (text === 'day before yesterday') return found(addDays(today, -2));

  match = text.match(/^(\d{1,4}|[a-z]+) (day|week|month|year)s? ago$/);
  if (match) {
    const count = /^\d+$/.test(match[1]) ? Number(match[1]) : COUNT_WORDS[match[1]];
    if (count) return found(shift(today, count, match[2]));
  }

  match = text.match(/^(?:last|previous) (week|month|year)$/);
  if (match) return found(shift(today, 1, match[1]));

  // "last Monday" (or just "Monday"): the most recent one before today
  match = text.match(/^(?:(?:last|previous|past) )?([a-z]+)$/);
  if (match && WEEKDAYS[match[1]] !== undefined) {
    const daysBack = (today.getDay() - WEEKDAYS[match[1]] + 7) % 7 || 7;
    return found(addDays(today, -daysBack));
  }

  // "17 September 2025" or "September 17 2025"; the year may be left out
  const dayFirst = text.match(/^(\d{1,2})(?:st|nd|rd|th)? (?:of )?([a-z]+)(?: (\d{4}))?$/);
  const monthFirst = text.match(/^([a-z]+) (\d{1,2})(?:st|nd|rd|th)?(?: (\d{4}))?$/);
  const named = dayFirst
    ? { day: dayFirst[1], month: dayFirst[2], year: dayFirst[3] }
    : monthFirst && { day: monthFirst[2], month: monthFirst[1], year: monthFirst[3] };
  if (named && MONTHS[named.month] !== undefined) {
    const day = Number(named.day);
    const monthIndex = MONTHS[named.month];
    if (named.year) {
      const date = makeDate(Number(named.year), monthIndex, day);
      return date ? found(date) : notReal;
    }
    // No year: the most recent time that day came round
    const thisYear = makeDate(today.getFullYear(), monthIndex, day);
    if (thisYear && thisYear <= today) return found(thisYear);
    const lastYear = makeDate(today.getFullYear() - 1, monthIndex, day);
    if (lastYear) return found(lastYear);
    return thisYear ? found(thisYear) : notReal;
  }

  if (/^\d{1,4}[/ -]\d{1,2}[/ -]\d{1,4}$/.test(text)) {
    return {
      error: 'Write the month as a word (17 Sep 2025) or use YYYY-MM-DD, so the day and month cannot be mixed up.'
    };
  }

  return { error: `Could not read that as a date. Try ${DATE_EXAMPLES}.` };
};

// "17 September 2025"
export const formatLongDate = (iso) =>
  new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(fromIsoDate(iso));

// Enough digits to be exact for large rates (16,463) and small ones (0.00006074).
export const formatRateNumber = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1000) return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  if (abs >= 1) return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value);
  return new Intl.NumberFormat('en-US', { maximumSignificantDigits: 4 }).format(value);
};

const formatAmountNumber = (value) =>
  Math.abs(value) >= 1
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)
    : new Intl.NumberFormat('en-US', { maximumSignificantDigits: 4 }).format(value);

const rateLine = ({ from, to, rate, rateDate, source }) =>
  `Rate on ${formatLongDate(rateDate)}: 1 ${from} = ${formatRateNumber(rate)} ${to} (${source})`;

const describeGap = (requestedDate, today) => {
  const weekday = fromIsoDate(requestedDate).getDay();
  if (weekday === 0 || weekday === 6) return 'a weekend';
  if (requestedDate >= toIsoDate(addDays(fromIsoDate(today), -1))) {
    return 'not published yet, or a non-trading day';
  }
  return 'a holiday or non-trading day';
};

// Every answer has the same parts:
//   notice   – only when the rate is not for the day that was asked for
//   headline – "100 EUR ≈ 16,306 JPY"
//   detail   – "Rate on 31 December 2024: 1 EUR = 163.06 JPY (source)"
export const describeHistoricalRate = (result, today = toIsoDate(new Date())) => {
  const { from, to, amount, rate } = result;
  const isUnitAmount = amount === 1;
  return {
    tone: result.isFallback ? 'notice' : 'ok',
    notice: result.isFallback
      ? `No rate was published for ${formatLongDate(result.requestedDate)} (${describeGap(result.requestedDate, today)}). The closest available rate is from ${formatLongDate(result.rateDate)}.`
      : '',
    headline: isUnitAmount
      ? `1 ${from} = ${formatRateNumber(rate)} ${to}`
      : `${formatAmountNumber(amount)} ${from} ≈ ${formatAmountNumber(amount * rate)} ${to}`,
    detail: rateLine(result)
  };
};

// Wording for the cases the server refuses: a future date or one before the data starts.
const describeRefusal = (err, amount) => {
  const body = err.body || {};
  if (body.code === 'future_date') {
    const latest = body.latest;
    if (!latest) return { tone: 'error', notice: 'Rates for future dates are not available.', headline: '', detail: '' };
    return {
      ...describeHistoricalRate({ ...latest, amount, isFallback: false }),
      tone: 'error',
      notice: `Rates for future dates are not available. The latest available rate is from ${formatLongDate(latest.rateDate)}.`
    };
  }
  return { tone: 'error', notice: err.message, headline: '', detail: '' };
};

// Looks up `amount` of `from` in `to` on `date` (YYYY-MM-DD). Always resolves
// to { tone, notice, headline, detail }; tone is 'ok', 'notice' or 'error'.
export const lookupHistoricalRate = async ({ from, to, amount = 1, date }, now = new Date()) => {
  const today = toIsoDate(now);
  const params = new URLSearchParams({ from, to, date, amount: String(amount), today });
  try {
    const result = await apiRequest(`/api/exchange-rate/history?${params.toString()}`);
    return describeHistoricalRate(result, today);
  } catch (err) {
    return describeRefusal(err, amount);
  }
};

const MAX_INVOICE_DATES = 24;

// Dates a rate can be pinned to, newest first: each invoice's own date when
// the invoice has one, otherwise the last day of its billing period.
export const getInvoiceDateOptions = (invoices) => {
  const seen = new Set();
  const options = [];
  (Array.isArray(invoices) ? invoices : []).forEach((invoice) => {
    if (!invoice) return;
    const stamp = String(invoice.date || invoice.created_at || '');
    const period = String(invoice.invoice_period || '').match(/^(\d{4})-(\d{2})$/);
    let date = null;
    if (/^\d{4}-\d{2}-\d{2}/.test(stamp)) {
      const [year, month, day] = stamp.slice(0, 10).split('-').map(Number);
      date = makeDate(year, month - 1, day);
    } else if (period) {
      date = new Date(Number(period[1]), Number(period[2]), 0);
    }
    if (!date) return;
    const value = toIsoDate(date);
    if (seen.has(value)) return;
    seen.add(value);
    const name = period
      ? `${new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' }).format(new Date(Number(period[1]), Number(period[2]) - 1, 1))} invoice`
      : 'Invoice';
    options.push({ value, label: `${name} · ${formatLongDate(value)}` });
  });
  return options.sort((a, b) => b.value.localeCompare(a.value)).slice(0, MAX_INVOICE_DATES);
};
